import styled from 'styled-components';
import { Button } from '@/ui';

/** Matches WorkspaceList activity variant outer shell (see CompactWorkspaceList wrapper). */
export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ActivityTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ActivityPeriod = styled.p`
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const ActivityRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  text-align: left;
  padding: 0.7rem 0.35rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}08`};
  }
`;

export const ActivityLeft = styled.button`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: flex-start;
  gap: 0.6rem;
  text-align: left;
  padding: 0;
  cursor: pointer;
`;

export const ActivityIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => `${theme.colors.primary}10`};
  flex-shrink: 0;
`;

export const ActivityItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  flex: 1;
`;

export const ActivityTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
`;

export const ActivityItemTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ActivityTimestamp = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  flex-shrink: 0;
`;

export const ActivityItemMeta = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ItemQuickActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  flex-shrink: 0;
`;

export const CompactActionButton = styled(Button)`
  white-space: nowrap;
  font-size: 0.75rem;
  padding: 0.35rem 0.5rem;
  min-height: auto;
`;

export const GroupFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &[data-in-accordion='true'] {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export const GroupFilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
`;

export const GroupFilterLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-right: 0.15rem;
`;

export const GroupTab = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  font-size: 0.78rem;
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};
  line-height: 1.2;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.text.primary : theme.colors.text.secondary};
  background-color: ${({ theme, $isActive }) =>
    $isActive ? `${theme.colors.primary}12` : 'transparent'};
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? `${theme.colors.primary}30` : theme.colors.border.light};
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}08`};
    border-color: ${({ theme }) => `${theme.colors.primary}25`};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const GroupTabCount = styled.span`
  font-size: 0.72rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.9;
`;

export const ListLessRow = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-align: left;
  padding: 0.55rem 0.35rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => `${theme.colors.primary}08`};
  }
`;

export const ListLessLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ListLessMeta = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

export const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.25rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`;

export const PaginationInfo = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 120px;
  text-align: center;
`;
